
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { Link } from '@/components/LocaleLink';

interface HeroProps {
  kicker?: string | string[];
  /** Ms each kicker line stays visible before rotating, by index. Falls back to DEFAULT_KICKER_DURATION. */
  kickerDurations?: number[];
  title: string | React.ReactNode;
  subtitle: string;
  ctaText?: string;
  ctaLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  /** Small second line inside the secondary CTA button (e.g. "20-minute call"). Omitted or empty = not rendered. */
  secondaryCtaNote?: string;
  className?: string;
  children?: React.ReactNode;
  showSwitchingText?: boolean;
  /** 'light' (default) keeps the existing white hero used on every other page. 'dark' switches to the brand guide's primary navy expression. */
  theme?: 'light' | 'dark';
}

const DEFAULT_KICKER_DURATION = 3500;

const Hero = ({
  kicker,
  kickerDurations,
  title,
  subtitle,
  ctaText = "Learn more", 
  ctaLink = "/services/ecommerce-automation",
  secondaryCtaText,
  secondaryCtaLink,
  secondaryCtaNote,
  className,
  children,
  showSwitchingText = false,
  theme = 'light'
}: HeroProps) => {
  const isDark = theme === 'dark';
  // Coral secondary CTA — brand accent token, same on every page (replaces the old hardcoded orange).
  // With a note, the button stacks label + note in two lines.
  const secondaryBtnClass = cn(
    "inline-flex items-center justify-center px-8 py-4 rounded-lg bg-accent text-accent-foreground text-base font-medium transition-all duration-300 hover:bg-accent/90 min-w-[160px] shadow-md",
    secondaryCtaNote && "flex-col py-3 px-6 sm:px-8"
  );
  const secondaryLabel = secondaryCtaNote ? (
    <>
      <span className="text-lg sm:text-xl whitespace-nowrap">{secondaryCtaText}</span>
      <span className="text-[10px] sm:text-[11px] font-normal opacity-80 whitespace-nowrap">{secondaryCtaNote}</span>
    </>
  ) : secondaryCtaText;
  // Primary CTA: solid navy-on-white for the light hero (unchanged); outline on the dark hero, since bg-foreground would render white-on-white there.
  const primaryBtnClass = isDark
    ? "inline-flex items-center justify-center px-8 py-4 rounded-md bg-transparent border border-white/20 text-white text-base font-medium transition-all duration-300 hover:bg-white/10 min-w-[160px]"
    : "inline-flex items-center justify-center px-8 py-4 rounded-md bg-foreground text-white text-base font-medium transition-all duration-300 hover:bg-foreground/90 min-w-[160px]";
  const heroRef = useRef<HTMLDivElement>(null);
  const [textIndex, setTextIndex] = useState(0);

  const kickerList = kicker ? (Array.isArray(kicker) ? kicker : [kicker]) : [];
  const kickerListRef = useRef(kickerList);
  kickerListRef.current = kickerList;
  const kickerDurationsRef = useRef(kickerDurations ?? []);
  kickerDurationsRef.current = kickerDurations ?? [];
  const [kickerIndex, setKickerIndex] = useState(0);
  const [kickerVisible, setKickerVisible] = useState(true);

  // Rotate through kicker lines (fade out, swap, fade in) when more than one is given.
  // Re-schedules on every index change so each line can hold its own duration.
  useEffect(() => {
    if (kickerList.length <= 1) return;
    const duration = kickerDurationsRef.current[kickerIndex] ?? DEFAULT_KICKER_DURATION;
    let fadeTimer: ReturnType<typeof setTimeout>;
    const holdTimer = setTimeout(() => {
      setKickerVisible(false);
      fadeTimer = setTimeout(() => {
        setKickerIndex(prev => (prev + 1) % kickerListRef.current.length);
        setKickerVisible(true);
      }, 600);
    }, duration);
    return () => {
      clearTimeout(holdTimer);
      clearTimeout(fadeTimer);
    };
  }, [kickerIndex, kickerList.length]);

  const switchingTexts = [
    "Product Management",
    "Pricing",
    "PPC management",
    "Campaign boosting",
    "Restocking",
    "Cannibal hunting"
  ];

  // Effect for switching text
  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex(prevIndex => (prevIndex + 1) % switchingTexts.length);
    }, 2000);
    
    return () => clearInterval(interval);
  }, [switchingTexts.length]);


  return (
    <div
      ref={heroRef}
      className={cn(
        "hero-root relative min-h-[350px] md:min-h-[479px] flex flex-col items-center justify-end text-center px-4 pt-[20rem] md:pt-16 pb-12 md:pb-12",
        isDark && "dark",
        className
      )}
    >
      {/* Background: white on every other page, brand navy for the dark hero */}
      <div className="absolute inset-0 bg-background"></div>

      {/* Thin orbital-arc texture — brand guide's dark-mode background treatment, used sparingly. Sits around the kicker, below the fixed header, so it stays fully visible */}
      {isDark && (
        <svg
          className="absolute top-28 md:top-24 left-1/2 -translate-x-1/2 w-[420px] max-w-none opacity-60"
          viewBox="0 0 420 150"
          aria-hidden="true"
        >
          <ellipse cx="210" cy="75" rx="190" ry="34" fill="none" stroke="var(--sd-coral)" strokeWidth="1" opacity="0.35" transform="rotate(-6 210 75)" />
          <ellipse cx="210" cy="75" rx="160" ry="26" fill="none" stroke="var(--sd-text-muted)" strokeWidth="1" opacity="0.3" transform="rotate(4 210 75)" />
        </svg>
      )}

      <div className="container relative max-w-4xl mx-auto z-10">
        <div className="relative left-0 mt-16 md:mt-0 flex flex-col items-center text-center">
          {/* Kicker - a middle ground: smaller and softer than the title, but still bold, not a tiny eyebrow.
              An invisible copy of the longest rotating line reserves the box's height (at whatever
              breakpoint/wrap it needs); the real text sits absolutely centered on top of it, so
              switching between a short and a long line never shifts the title below. */}
          {kickerList.length > 0 && (
            <div className="relative mb-6 md:mb-8">
              <p
                aria-hidden="true"
                className="hero-kicker invisible text-base sm:text-lg md:text-2xl font-semibold leading-snug text-center"
              >
                {kickerList.reduce((a, b) => (b.length > a.length ? b : a), kickerList[0])}
              </p>
              <p
                className={cn(
                  "hero-kicker absolute inset-0 flex items-start justify-center text-base sm:text-lg md:text-2xl font-semibold leading-snug text-foreground/70 text-center transition-opacity duration-500",
                  kickerVisible ? "opacity-100" : "opacity-0"
                )}
              >
                {kickerList[kickerIndex % kickerList.length]}
              </p>
            </div>
          )}

          {/* Title - smaller on mobile only; desktop = original size; one line; centered */}
          <h1 className="hero-title text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.15] whitespace-normal md:whitespace-nowrap">
            {title}
          </h1>
          
          {/* Subtitle - extra spacing on mobile only; 2 lines; left-aligned, full width of the content container */}
          <p className="hero-subtitle text-base md:text-lg text-muted-foreground mt-5 mb-4 md:mt-4 md:mb-0 leading-relaxed w-full text-center">
            {subtitle.split('\n').map((line, i) => (
              <span key={i} className="block">
                {line}
              </span>
            ))}
          </p>
          
          {/* CTA buttons - more top margin on mobile for spacing below subtitle */}
          <div className="mt-10 md:mt-12 mb-0 flex flex-wrap justify-center gap-8 md:gap-8 pb-4">
            {secondaryCtaText && secondaryCtaLink && (
              secondaryCtaLink.startsWith('#') ? (
                <a
                  href={secondaryCtaLink}
                  className={secondaryBtnClass}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(secondaryCtaLink.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {secondaryLabel}
                </a>
              ) : (
                <Link
                  to={secondaryCtaLink}
                  className={secondaryBtnClass}
                >
                  {secondaryLabel}
                </Link>
              )
            )}
            {ctaText && ctaLink && (
              ctaLink.startsWith('#') ? (
                <a
                  href={ctaLink}
                  className={primaryBtnClass}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(ctaLink.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {ctaText}
                </a>
              ) : (
                <Link
                  to={ctaLink}
                  className={primaryBtnClass}
                >
                  {ctaText}
                </Link>
              )
            )}
          </div>
        </div>
        
        {/* Optional additional content */}
        {children && (
          <div className="mt-24 md:mt-28">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default Hero;
